import Cookies from 'js-cookie';

import { axiosMicroservice } from 'src/api/axios';

import {
  SNAPCHAT_PIXEL_ID,
  SNAP_BACKEND_VARIANTS_MAP,
} from '@features/Analytics/constants';

import type { TpSnapchatEvent } from '../types/snapchat';

const FIRST_CLASS_OPTION_KEYS = [
  'test_event_code',
  'event_source_url',
  'email',
  'phone',
  'ph',
  'external_id',
  'sc_cookie1',
  'client_ip_address',
  'client_user_agent',
  'value',
  'amount',
  'currency',
  'content_ids',
  'content_category',
  'number_items',
] as const;

const SNAPCHAT_CLICK_ID_KEY = 'ScCid';

const getStoredSnapchatClickId = () => {
  try {
    return sessionStorage.getItem(SNAPCHAT_CLICK_ID_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

const getSnapchatClickId = (fallbackClickId?: string) => {
  if (typeof window === 'undefined') return fallbackClickId;

  const searchParameters = new URLSearchParams(window.location.search);
  const urlClickId =
    searchParameters.get('ScCid') ?? searchParameters.get('sccid');

  if (urlClickId) {
    try {
      sessionStorage.setItem(SNAPCHAT_CLICK_ID_KEY, urlClickId);
    } catch {
      // The current event can still use the URL value when storage is blocked.
    }

    return urlClickId;
  }

  return fallbackClickId || getStoredSnapchatClickId();
};

const getSnapchatBrowserData = () => {
  if (typeof window === 'undefined') return {};

  return {
    event_source_url: window.location.href || undefined,
    client_user_agent: window.navigator.userAgent || undefined,
    sc_cookie1:
      typeof document !== 'undefined' ? Cookies.get('_scid') : undefined,
    external_id:
      typeof document !== 'undefined' ? Cookies.get('extId') : undefined,
  };
};

const removeUndefinedValues = <T extends Record<string, any>>(value: T) =>
  Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => entryValue !== undefined),
  );

const splitSnapchatOptions = (options: TpSnapchatEvent['options'] = {}) => {
  const firstClassData: Record<string, any> = {};
  const customData: Record<string, any> =
    typeof options.custom_data === 'object' && options.custom_data != null
      ? { ...options.custom_data }
      : {};

  for (const [key, value] of Object.entries(options)) {
    if (key === 'custom_data') continue;

    if ((FIRST_CLASS_OPTION_KEYS as readonly string[]).includes(key)) {
      firstClassData[key] = value;
      continue;
    }

    customData[key] = value;
  }

  const normalizedCustomData = removeUndefinedValues(customData);

  return {
    firstClassData: removeUndefinedValues(firstClassData),
    customData:
      Object.keys(normalizedCustomData).length > 0
        ? normalizedCustomData
        : undefined,
  };
};

export const postSnapchatEvent = async ({
  eventName,
  eventId,
  sc_click_id,
  options,
}: TpSnapchatEvent) => {
  try {
    const origin = SNAP_BACKEND_VARIANTS_MAP.get(SNAPCHAT_PIXEL_ID) || 'mimika';
    const { firstClassData, customData } = splitSnapchatOptions(options);
    const browserData = getSnapchatBrowserData();

    const { data } = await axiosMicroservice.post(
      'snapchat_event',
      removeUndefinedValues({
        event_name: eventName,
        event_id: eventId,
        sc_click_id: getSnapchatClickId(sc_click_id),
        origin,
        ...browserData,
        ...firstClassData,
        custom_data: customData,
      }),
    );
    return data;
  } catch (error) {
    console.log(error);
  }
};
